/**
 * AmbienteRise.jsx · v101.147.0
 * Integraciones › Ambiente RISE: prender / apagar cada integración externa en el ambiente de pruebas y
 * sincronizar a mano desde el espejo de Odoo. En producción solo informa que no aplica.
 * v101.158.0 · Hacia Odoo solo manual · WhatsApp solo a «Números de prueba» · espejo de Odoo = servidor de pruebas.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { FlaskConical, Loader2, RefreshCw, Database, CheckCircle2, XCircle, PlayCircle, Clock, MessageCircle, Trash2, Plus, Hand } from "lucide-react";
import Layout from "../components/Layout";
import api from "../lib/api";
import { Switch } from "../components/ui/switch";

const SYNC_HELP = {
  odoo_orders_sync: "OTs nuevas y cambios desde el espejo",
  odoo_passive_equipment: "Cajas, OLTs y CPEs",
  odoo_inventory_sync: "Stock por bodega",
  odoo_equipment_ports: "Puertos de cajas y radio bases (pesado)",
  categories_sync: "Categorías de productos",
  ot_materialize: "Recalcula la tabla de OTs",
  zombie_purge: "Limpia OTs huérfanas",
};

export default function AmbienteRise() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [waNum, setWaNum] = useState("");
  const [waName, setWaName] = useState("");

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/v1/entorno/integraciones");
      setData(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cargar el ambiente RISE");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (it, value) => {
    const verb = value ? "PRENDER" : "APAGAR";
    const extra = value && ["olt", "axxess", "odoo_ws", "odoo_rpc", "odoo_webhook"].includes(it.code)
      ? "\n\nATENCIÓN: esta integración actúa sobre sistemas REALES (Odoo / red / routers de clientes)." : "";
    if (!window.confirm(`¿${verb} «${it.name}» en el ambiente RISE?${extra}`)) return;
    setBusy(it.code);
    try {
      await api.put(`/v1/entorno/integraciones/${it.code}`, { enabled: value });
      toast.success(`${it.name}: ${value ? "prendida" : "apagada"}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo guardar");
    } finally { setBusy(null); }
  };

  const sync = async (code) => {
    setBusy(`sync-${code}`);
    try {
      const { data: d } = await api.post(`/v1/entorno/sincronizar/${code}`);
      toast.success(d?.mensaje || "Sincronización pedida");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo pedir la sincronización");
    } finally { setBusy(null); }
  };

  const addWa = async () => {
    setBusy("wa-add");
    try {
      await api.post("/v1/entorno/whatsapp-numeros", { numero: waNum.trim(), nombre: waName.trim() });
      toast.success("Número de prueba agregado");
      setWaNum(""); setWaName("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo agregar");
    } finally { setBusy(null); }
  };

  const delWa = async (n) => {
    if (!window.confirm(`¿Quitar ${n} de los números de prueba?`)) return;
    setBusy(`wa-${n}`);
    try {
      await api.delete(`/v1/entorno/whatsapp-numeros/${encodeURIComponent(n)}`);
      toast.success("Número quitado");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo quitar");
    } finally { setBusy(null); }
  };

  if (loading) {
    return <Layout><div className="p-8 text-center text-xs text-slate-500"><Loader2 size={14} className="animate-spin inline mr-2" />Cargando…</div></Layout>;
  }

  if (!data?.rise) {
    return (
      <Layout>
        <div className="max-w-2xl mx-auto p-6 text-sm text-slate-600" data-testid="rise-not-active">
          <h1 className="text-lg font-bold text-[#0A3747] mb-2 flex items-center gap-2"><FlaskConical size={18} /> Ambiente RISE</h1>
          Este servidor es <b>producción</b>: todas las integraciones están activas y este panel no aplica. El panel
          funciona en el ambiente de pruebas RISE (servidor con AFC_ENTORNO=rise y bases rise_…).
        </div>
      </Layout>
    );
  }

  const canEdit = !!data.can_edit;
  const bases = data.bases || {};
  return (
    <Layout>
      <div className="max-w-4xl mx-auto p-4 space-y-5" data-testid="rise-page">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-lg font-bold text-[#0A3747] flex items-center gap-2"><FlaskConical size={18} /> Ambiente de pruebas RISE</h1>
            <p className="text-xs text-slate-500 max-w-2xl">
              Aquí se prueba todo antes de pasarlo a producción, sobre una COPIA de la base de AlfaField y los servidores
              de PRUEBAS de Alfanet. Las integraciones nacen apagadas. Hacia Odoo todo es manual: solo se envía cuando
              una persona hace la acción (cerrar, aprovisionar, cancelar…); los procesos automáticos no llaman a Odoo.
              WhatsApp solo sale a los números de prueba de abajo. El espejo de Odoo solo se lee con «Sincronizar ahora».
            </p>
          </div>
          <button onClick={() => { setLoading(true); load(); }} className="px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-bold text-slate-600 flex items-center gap-1.5 hover:bg-slate-50">
            <RefreshCw size={12} /> Actualizar
          </button>
        </div>

        <div className="grid sm:grid-cols-2 gap-3" data-testid="rise-bases">
          <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 flex items-center gap-2 text-xs">
            <Database size={14} className="text-slate-400" />
            <span className="flex-1">Base de AlfaField (copia rise_…)</span>
            {bases.alfafield_rise ? <span className="text-emerald-700 font-bold flex items-center gap-1"><CheckCircle2 size={13} />Copia RISE</span>
              : <span className="text-red-600 font-bold flex items-center gap-1"><XCircle size={13} />Sin prefijo RISE</span>}
          </div>
          <div className="rounded-xl border border-slate-200 bg-white px-3 py-2 flex items-center gap-2 text-xs">
            <Database size={14} className="text-slate-400" />
            <span className="flex-1">Espejo de Odoo (solo lectura)</span>
            {!bases.odoo_espejo ? <span className="text-amber-600 font-bold flex items-center gap-1"><XCircle size={13} />Sin configurar</span>
              : bases.odoo_espejo_pruebas === false
                ? <span className="text-amber-600 font-bold flex items-center gap-1" title="Mientras Alfanet no entregue el espejo de pruebas, se LEE el de producción (no se escribe)"><CheckCircle2 size={13} />Producción (solo lectura)</span>
                : <span className="text-emerald-700 font-bold flex items-center gap-1"><CheckCircle2 size={13} />Servidor de pruebas</span>}
          </div>
          <div className="sm:col-span-2 rounded-xl border border-sky-200 bg-sky-50 px-3 py-2 flex items-center gap-2 text-xs text-sky-800" data-testid="rise-odoo-manual">
            <Hand size={14} />
            <span><b>Hacia Odoo: solo manual.</b> Con «Odoo» prendido, se envía únicamente lo que una persona hace en la web
              o la app; reintentos, colas y agentes automáticos no llaman a Odoo en RISE.</span>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto" data-testid="rise-integraciones">
          <div className="px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-slate-500 bg-slate-50">Integraciones</div>
          <table className="w-full text-xs min-w-[520px]">
            <tbody className="divide-y divide-slate-100">
              {(data.items || []).map(it => (
                <tr key={it.code} data-testid={`rise-int-${it.code}`}>
                  <td className="px-3 py-2">
                    <div className="font-bold text-[#0A3747]">{it.name}</div>
                    <div className="text-[10.5px] text-slate-500">{it.desc}</div>
                    {it.pruebas_configurado === false && (
                      <div className="text-[10.5px] font-bold text-red-600" data-testid={`rise-prod-${it.code}`}>
                        Apunta a PRODUCCIÓN: no se puede prender hasta cargar su servidor de pruebas en el archivo RISE.
                      </div>
                    )}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <div className="inline-flex items-center gap-2">
                      {busy === it.code ? <Loader2 size={13} className="animate-spin text-slate-400" /> : (
                        <Switch checked={!!it.enabled} disabled={!canEdit || !!busy || it.pruebas_configurado === false} onCheckedChange={v => toggle(it, v)}
                          data-testid={`rise-switch-${it.code}`} />
                      )}
                      <span className={`text-[10px] font-bold ${it.enabled ? "text-emerald-700" : "text-red-600"}`}>{it.enabled ? "Prendida" : "Apagada"}</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto" data-testid="rise-whatsapp">
          <div className="px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-slate-500 bg-slate-50 flex items-center gap-1.5">
            <MessageCircle size={12} /> WhatsApp · números de prueba
          </div>
          <div className="px-3 py-2 text-[10.5px] text-slate-500">
            Con WhatsApp prendido, los mensajes salen SOLO a estos números. A cualquier otro número (clientes reales) no se
            envía nada; queda en el log.
          </div>
          <table className="w-full text-xs min-w-[420px]">
            <tbody className="divide-y divide-slate-100">
              {(data.whatsapp_numeros || []).map(w => (
                <tr key={w.numero} data-testid={`rise-wa-${w.numero}`}>
                  <td className="px-3 py-2 font-mono">{w.numero}</td>
                  <td className="px-3 py-2">{w.nombre || "—"}</td>
                  <td className="px-3 py-2 text-[10.5px] text-slate-500">{w.added_by || ""}</td>
                  <td className="px-3 py-2 text-right">
                    {canEdit && (
                      <button onClick={() => delWa(w.numero)} disabled={!!busy} className="p-1 rounded text-red-500 hover:bg-red-50 disabled:opacity-50" title="Quitar">
                        <Trash2 size={13} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {(data.whatsapp_numeros || []).length === 0 && (
                <tr><td colSpan={4} className="px-3 py-3 text-center text-slate-400 italic">Sin números: no se envía WhatsApp a nadie</td></tr>
              )}
            </tbody>
          </table>
          {canEdit && (
            <div className="px-3 py-2 flex items-center gap-2 flex-wrap border-t border-slate-100">
              <input value={waNum} onChange={e => setWaNum(e.target.value)} placeholder="0991234567 o +593991234567"
                className="px-2 py-1.5 rounded border border-slate-200 text-xs font-mono w-52" data-testid="rise-wa-numero" />
              <input value={waName} onChange={e => setWaName(e.target.value)} placeholder="Nombre (opcional)"
                className="px-2 py-1.5 rounded border border-slate-200 text-xs w-44" data-testid="rise-wa-nombre" />
              <button onClick={addWa} disabled={!!busy || waNum.trim().length < 9}
                className="px-2.5 py-1.5 rounded-md bg-[#0A3747] text-[#E0E621] text-[10.5px] font-bold disabled:opacity-50 flex items-center gap-1"
                data-testid="rise-wa-add">
                <Plus size={12} /> Agregar
              </button>
            </div>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto" data-testid="rise-sync">
          <div className="px-3 py-2 text-[11px] font-bold uppercase tracking-wider text-slate-500 bg-slate-50 flex items-center justify-between gap-2 flex-wrap">
            <span>Sincronización manual desde el espejo de Odoo</span>
            {canEdit && (
              <button onClick={() => sync("todas")} disabled={!!busy}
                className="px-2.5 py-1 rounded-md bg-[#0A3747] text-[#E0E621] text-[10.5px] font-bold normal-case tracking-normal disabled:opacity-50 flex items-center gap-1"
                data-testid="rise-sync-all">
                <PlayCircle size={12} /> Sincronizar todo
              </button>
            )}
          </div>
          <table className="w-full text-xs min-w-[520px]">
            <tbody className="divide-y divide-slate-100">
              {(data.triggers || []).map(t => (
                <tr key={t.code} data-testid={`rise-sync-${t.code}`}>
                  <td className="px-3 py-2">
                    <div className="font-bold text-[#0A3747]">{t.name}</div>
                    <div className="text-[10.5px] text-slate-500">{SYNC_HELP[t.code] || t.code}</div>
                  </td>
                  <td className="px-3 py-2 text-[10.5px] text-slate-500 whitespace-nowrap">
                    <Clock size={11} className="inline mr-1" />{t.last_run ? new Date(t.last_run).toLocaleString("es-EC") : "Nunca"}
                    {t.pendiente && <span className="ml-2 text-amber-600 font-bold">pedida</span>}
                    {t.last_error && <div className="text-red-600 max-w-[260px] truncate" title={t.last_error}>{t.last_error}</div>}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {canEdit && (
                      <button onClick={() => sync(t.code)} disabled={!!busy || t.pendiente}
                        className="px-2.5 py-1 rounded-md border border-slate-200 text-[10.5px] font-bold text-[#0A3747] hover:bg-slate-50 disabled:opacity-50"
                        data-testid={`rise-sync-btn-${t.code}`}>
                        {busy === `sync-${t.code}` ? <Loader2 size={11} className="animate-spin inline" /> : "Sincronizar ahora"}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {(data.triggers || []).length === 0 && (
                <tr><td className="px-3 py-4 text-center text-slate-400 italic">Sin sincronizaciones registradas</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <p className="text-[10.5px] text-slate-500">
          Las sincronizaciones leen el espejo de Odoo del servidor de PRUEBAS (variables del ambiente) y solo corren con
          «Sincronizar ahora». La copia de la base de AlfaField (rise_alfafield) se refresca a mano desde producción con el script rise_crear_base.py --apply --reemplazar.
        </p>
      </div>
    </Layout>
  );
}
